<template>
  <div class="fp-root">
    <!-- 输入区 -->
    <div class="fp-input-row">
      <span class="fp-label">y =</span>
      <el-input v-model="inputExpr" size="small" placeholder="如: sin(x), x^2-4*x+3" @keyup.enter="addFunction" class="fp-input" />
      <span class="fp-color-dot" :style="{ background: nextColor }"></span>
      <el-button size="small" type="primary" @click="addFunction">{{ $t("common.action.add") }}</el-button>
    </div>

    <!-- 预设 -->
    <div class="fp-presets">
      <span class="preset-label">{{ $t('tool.FunctionPlot.basic') }}：</span>
      <span v-for="ex in presets.basic" :key="ex" class="preset-chip" @click="inputExpr=ex;addFunction()">{{ ex }}</span>
      <span class="preset-label" style="margin-left:8px">{{ $t('tool.FunctionPlot.trig') }}：</span>
      <span v-for="ex in presets.trig" :key="ex" class="preset-chip" @click="inputExpr=ex;addFunction()">{{ ex }}</span>
      <span class="preset-label" style="margin-left:8px">{{ $t('tool.FunctionPlot.exp') }}：</span>
      <span v-for="ex in presets.exp" :key="ex" class="preset-chip" @click="inputExpr=ex;addFunction()">{{ ex }}</span>
    </div>

    <!-- 范围控制 -->
    <div class="fp-range">
      <span class="range-label">{{ $t('tool.FunctionPlot.xRange') }}：</span>
      <el-input-number v-model="xMin" :step="1" size="mini" controls-position="right" style="width:90px" @change="replot" />
      <span class="range-sep">~</span>
      <el-input-number v-model="xMax" :step="1" size="mini" controls-position="right" style="width:90px" @change="replot" />
      <el-button size="mini" @click="autoFit" style="margin-left:8px">{{ $t('tool.FunctionPlot.autoFit') }}</el-button>
      <span class="range-label" style="margin-left:12px">{{ $t('tool.FunctionPlot.yRange') }}：</span>
      <span class="range-val">[{{ fmt(yMin) }}, {{ fmt(yMax) }}]</span>
      <el-checkbox v-model="lockY" size="mini" style="margin-left:8px">{{ $t('tool.FunctionPlot.lockY') }}</el-checkbox>
    </div>

    <!-- 画布 -->
    <div class="fp-canvas-wrap" @wheel="onWheel" @mousemove="onMouseMove" @mouseleave="cursor=null">
      <canvas ref="canvas" :width="W" :height="H" />
      <div class="fp-cursor" v-if="cursor">{{ cursor }}</div>
    </div>

    <!-- 函数列表 -->
    <div class="fp-list" v-if="functions.length">
      <div v-for="(fn, i) in functions" :key="fn.id" class="fp-fn-row">
        <span class="fp-fn-dot" :style="{ background: fn.visible ? fn.color : '#ccc' }"></span>
        <span class="fp-fn-expr" :class="{ dim: !fn.visible }">y = {{ fn.expression }}</span>
        <span class="fp-fn-acts">
          <el-button size="mini" type="text" @click="fn.visible=!fn.visible;replot()">{{ fn.visible ? $t('tool.FunctionPlot.hide') : $t('tool.FunctionPlot.show') }}</el-button>
          <el-button size="mini" type="text" class="del" @click="removeFunction(i)">{{ $t("common.action.delete") }}</el-button>
        </span>
      </div>
    </div>

    <!-- 底部操作 -->
    <div class="fp-actions">
      <el-button size="mini" plain icon="el-icon-picture-outline" @click="exportPng">{{ $t('tool.FunctionPlot.exportPng') }}</el-button>
      <el-button size="mini" plain icon="el-icon-document" @click="exportSvg">{{ $t('tool.FunctionPlot.exportSvg') }}</el-button>
      <el-button size="mini" plain icon="el-icon-delete" @click="clearAll">{{ $t("common.action.clear") }}</el-button>
      <span class="fp-hint">{{ $t('tool.FunctionPlot.cursorHint') }}</span>
    </div>

    <div class="fp-error" v-if="errorMsg">{{ errorMsg }}</div>
  </div>
</template>

<script>
var COLORS = ['#e74c3c', '#4e7cff', '#67c23a', '#e6a23c', '#9b59b6', '#1abc9c']
var cid = 0

export default {
  name: 'FunctionPlotTool',
  props: { config: { type: Object, default: function() { return {} } } },
  data: function() {
    return {
      W: 780, H: 460,
      inputExpr: '',
      functions: [],
      xMin: -6, xMax: 6, yMin: -4, yMax: 4,
      lockY: false,
      cursor: null,
      errorMsg: '',
      presets: {
        basic: ['x^2', 'x^3', 'x^2-4*x+3', '1/x', 'sqrt(x)', 'abs(x)'],
        trig: ['sin(x)', 'cos(x)', 'tan(x)', '2*sin(3*x)+1', 'sin(x)/x'],
        exp: ['exp(x)', 'ln(x)', 'exp(-x^2)', '2^x']
      }
    }
  },

  computed: {
    nextColor: function() { return COLORS[this.functions.length % COLORS.length] }
  },

  mounted: function() {
    var self = this
    this.$nextTick(function() {
      self.addPreset('sin(x)')
      self.inputExpr = ''
    })
  },

  methods: {
    fmt: function(n) { return Math.abs(n) < 1e-10 ? 0 : (Math.abs(n) >= 1e4 || (Math.abs(n) < 0.01 && n !== 0) ? n.toExponential(2) : parseFloat(n.toFixed(4))) },

    addPreset: function(expr) {
      this.functions.push({
        id: ++cid,
        expression: expr,
        color: COLORS[(this.functions.length) % COLORS.length],
        visible: true,
        lineWidth: 2,
        fn: this.compile(expr)
      })
      this.replot()
    },

    addFunction: function() {
      var expr = this.inputExpr.trim()
      if (!expr) return
      var fn = this.compile(expr)
      if (!fn) return
      this.functions.push({
        id: ++cid,
        expression: expr,
        color: COLORS[this.functions.length % COLORS.length],
        visible: true,
        lineWidth: 2,
        fn: fn
      })
      this.inputExpr = ''
      this.replot()
    },

    removeFunction: function(i) { this.functions.splice(i, 1); this.replot() },

    clearAll: function() { this.functions = []; this.replot() },

    compile: function(body) {
      this.errorMsg = ''
      body = body.replace(/\^/g, '**')
        .replace(/(\d)([a-zA-Z])/g, '$1*$2')
        .replace(/\)\(/g, ')*(').replace(/(\d)\(/g, '$1*(')
        .replace(/\)(\d)/g, ')*$1')
        .replace(/(?<!\w)([a-zA-Z])\(/g, '$1*(')  // 仅单字母变量后才补乘号
      try {
        var code = 'const { sin, cos, tan, sqrt, abs, log, exp, pow, PI, E, floor, ceil, round, sign, log2, log10, max, min } = Math; return (' + body + ')'
        return new Function('x', code)
      } catch(e) { this.errorMsg = this.$t('tool.FunctionPlot.errSyntax') + ': ' + e.message; return null }
    },

    // ── 渲染 ─────────────────────────────
    toX: function(x) { var pad = 55; return pad + (x - this.xMin) / (this.xMax - this.xMin) * (this.W - pad * 2) },
    toY: function(y) { var pad = 55; return pad + (this.yMax - y) / (this.yMax - this.yMin) * (this.H - pad * 2) },

    computeAutoY: function() {
      var lo = Infinity, hi = -Infinity, self = this
      this.functions.forEach(function(fn) {
        if (!fn.visible || !fn.fn) return
        var step = (self.xMax - self.xMin) / 1000
        for (var x = self.xMin; x <= self.xMax; x += step) {
          var y = fn.fn(x)
          if (!isNaN(y) && isFinite(y) && Math.abs(y) < 1e6) { if (y < lo) lo = y; if (y > hi) hi = y }
        }
      })
      if (!isFinite(lo)) { lo = -4; hi = 4 }
      var pad = Math.max((hi - lo) * 0.15, 0.5)
      var l = lo - pad, h = hi + pad
      if (h - l < 2) { var m = (l + h) / 2; l = m - 1; h = m + 1 }
      var span = h - l
      var mag = Math.pow(10, Math.floor(Math.log10(span)))
      var s = mag; if (span / mag <= 2) s = mag * 0.5; else if (span / mag <= 5) s = mag; else s = mag * 2
      this.yMin = Math.floor(l / s) * s
      this.yMax = Math.ceil(h / s) * s
    },

    autoFit: function() { this.lockY = false; this.replot() },

    replot: function() {
      var c = this.$refs.canvas; if (!c) return
      // 重编译所有函数
      var self = this
      this.functions.forEach(function(fn) { fn.fn = self.compile(fn.expression) })
      if (!this.lockY) this.computeAutoY()
      this.draw()
    },

    draw: function() {
      var c = this.$refs.canvas, ctx = c.getContext('2d')
      var W = this.W, H = this.H, pad = 55, self = this
      ctx.clearRect(0, 0, W, H)
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H)

      // 网格步长
      var xSpan = this.xMax - this.xMin
      var xMag = Math.pow(10, Math.floor(Math.log10(xSpan)))
      var xStep = xMag; if (xSpan / xMag <= 2) xStep = xMag * 0.5; else if (xSpan / xMag <= 5) xStep = xMag; else xStep = xMag * 2
      var ySpan = this.yMax - this.yMin
      var yMag = Math.pow(10, Math.floor(Math.log10(ySpan)))
      var yStep = yMag; if (ySpan / yMag <= 2) yStep = yMag * 0.5; else if (ySpan / yMag <= 5) yStep = yMag; else yStep = yMag * 2
      if (yStep < 0.1) yStep = 1

      // 网格
      ctx.strokeStyle = '#f0f2f5'; ctx.lineWidth = 0.5
      for (var gx = Math.floor(this.xMin / xStep) * xStep; gx <= this.xMax + xStep * 0.5; gx += xStep) {
        ctx.beginPath(); ctx.moveTo(this.toX(gx), pad); ctx.lineTo(this.toX(gx), H - pad); ctx.stroke()
      }
      for (var gy = Math.floor(this.yMin / yStep) * yStep; gy <= this.yMax + yStep * 0.5; gy += yStep) {
        ctx.beginPath(); ctx.moveTo(pad, this.toY(gy)); ctx.lineTo(W - pad, this.toY(gy)); ctx.stroke()
      }

      // 坐标轴
      ctx.strokeStyle = '#999'; ctx.lineWidth = 1.2
      if (0 >= this.yMin && 0 <= this.yMax) {
        ctx.beginPath(); ctx.moveTo(pad, this.toY(0)); ctx.lineTo(W - pad, this.toY(0)); ctx.stroke()
        // 箭头
        ctx.beginPath(); ctx.moveTo(W - pad + 4, this.toY(0)); ctx.lineTo(W - pad - 6, this.toY(0) - 4); ctx.lineTo(W - pad - 6, this.toY(0) + 4); ctx.fillStyle = '#999'; ctx.fill()
      }
      if (0 >= this.xMin && 0 <= this.xMax) {
        ctx.beginPath(); ctx.moveTo(this.toX(0), pad); ctx.lineTo(this.toX(0), H - pad); ctx.stroke()
        ctx.beginPath(); ctx.moveTo(this.toX(0), pad - 4); ctx.lineTo(this.toX(0) - 4, pad + 6); ctx.lineTo(this.toX(0) + 4, pad + 6); ctx.fill()
      }

      // 刻度
      ctx.fillStyle = '#888'; ctx.font = '11px -apple-system,sans-serif'; ctx.textAlign = 'center'
      var axisY = (0 >= this.yMin && 0 <= this.yMax) ? this.toY(0) : H - pad
      for (var tx = Math.floor(this.xMin / xStep) * xStep; tx <= this.xMax + xStep * 0.5; tx += xStep) {
        if (Math.abs(tx) < xStep * 0.01) continue
        ctx.fillText(this.fmt(tx), this.toX(tx), axisY + 16)
        ctx.beginPath(); ctx.moveTo(this.toX(tx), axisY - 3); ctx.lineTo(this.toX(tx), axisY + 3); ctx.stroke()
      }
      ctx.textAlign = 'right'
      for (var ty = Math.floor(this.yMin / yStep) * yStep; ty <= this.yMax + yStep * 0.5; ty += yStep) {
        if (Math.abs(ty) < yStep * 0.01) continue
        ctx.fillText(this.fmt(ty), this.toX(0) - 8, this.toY(ty) + 4)
      }
      if (0 >= this.xMin && 0 <= this.xMax && 0 >= this.yMin && 0 <= this.yMax) {
        ctx.fillText('O', this.toX(0) - 12, this.toY(0) + 14)
      }
      ctx.textAlign = 'left'
      ctx.fillText('x', W - pad + 4, axisY + 4)
      ctx.fillText('y', (0 >= this.xMin && 0 <= this.xMax) ? this.toX(0) + 6 : W - pad + 6, pad - 6)

      // 裁剪区
      ctx.save(); ctx.beginPath(); ctx.rect(pad, pad, W - pad * 2, H - pad * 2); ctx.clip()

      // 曲线
      this.functions.forEach(function(fn) {
        if (!fn.visible || !fn.fn) return
        ctx.strokeStyle = fn.color; ctx.lineWidth = fn.lineWidth; ctx.lineJoin = 'round'
        ctx.beginPath()
        var started = false, step = (self.xMax - self.xMin) / 1200
        for (var x = self.xMin; x <= self.xMax; x += step) {
          var y = fn.fn(x)
          if (isNaN(y) || !isFinite(y) || Math.abs(y) > 1e8) { started = false; continue }
          var py = self.toY(y)
          if (py < pad - 2 || py > H - pad + 2) { started = false; continue }
          if (!started) { ctx.moveTo(self.toX(x), py); started = true }
          else ctx.lineTo(self.toX(x), py)
        }
        ctx.stroke()
      })

      ctx.restore()
    },

    // ── 交互 ─────────────────────────────
    onMouseMove: function(e) {
      var c = this.$refs.canvas, rect = c.getBoundingClientRect()
      var s = c.width / rect.width
      var mx = (e.clientX - rect.left) * s, my = (e.clientY - rect.top) * s
      var pad = 55
      var x = this.xMin + (mx - pad) / (this.W - pad * 2) * (this.xMax - this.xMin)
      var y = this.yMax - (my - pad) / (this.H - pad * 2) * (this.yMax - this.yMin)
      if (x < this.xMin || x > this.xMax) { this.cursor = null; return }
      this.cursor = 'x=' + x.toFixed(3) + '  y=' + y.toFixed(3)
      // 显示函数值
      var self = this
      this.functions.forEach(function(fn) {
        if (!fn.visible || !fn.fn) return
        var fy = fn.fn(x)
        if (!isNaN(fy) && isFinite(fy) && Math.abs(fy) < 1e6) {
          self.cursor += '  f=' + self.fmt(fy)
        }
      })
    },

    onWheel: function(e) {
      e.preventDefault()
      var mid = (this.xMin + this.xMax) / 2
      var half = (this.xMax - this.xMin) / 2 * (e.deltaY > 0 ? 1.3 : 0.7)
      this.xMin = mid - half; this.xMax = mid + half
      if (!this.lockY) this.computeAutoY()
      this.draw()
    },

    // ── 导出 ─────────────────────────────
    exportPng: function() {
      var c = this.$refs.canvas
      var a = document.createElement('a')
      a.href = c.toDataURL('image/png'); a.download = 'function-plot.png'
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
    },

    exportSvg: function() {
      var self = this, pad = 55, W = this.W, H = this.H
      var lines = ['<svg xmlns="http://www.w3.org/2000/svg" width="' + W + '" height="' + H + '">']
      lines.push('<rect width="' + W + '" height="' + H + '" fill="#fff"/>')

      // 曲线转换为SVG path
      this.functions.forEach(function(fn) {
        if (!fn.visible || !fn.fn) return
        var d = '', started = false
        var step = (self.xMax - self.xMin) / 800
        for (var x = self.xMin; x <= self.xMax; x += step) {
          var y = fn.fn(x)
          if (isNaN(y) || !isFinite(y)) { started = false; continue }
          var sx = self.toX(x), sy = self.toY(y)
          if (!started) { d += 'M' + sx.toFixed(1) + ',' + sy.toFixed(1); started = true }
          else d += 'L' + sx.toFixed(1) + ',' + sy.toFixed(1)
        }
        lines.push('<path d="' + d + '" fill="none" stroke="' + fn.color + '" stroke-width="' + fn.lineWidth + '"/>')
      })
      lines.push('</svg>')
      var blob = new Blob([lines.join('\n')], { type: 'image/svg+xml' })
      var url = URL.createObjectURL(blob)
      var a = document.createElement('a'); a.href = url; a.download = 'function-plot.svg'
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },

    doAction: function(a) {
      if (a === 'clear') this.clearAll()
      if (a === 'sample') { this.clearAll(); this.addPreset('x^2-4*x+3'); this.addPreset('sin(x)') }
    }
  }
}
</script>

<style scoped>
.fp-root { max-width: 820px; margin: 0 auto; }

.fp-input-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.fp-label { font-size: 18px; font-weight: 700; color: #1a1f36; font-style: italic; flex-shrink: 0; }
.fp-input { flex: 1; }
.fp-color-dot { width: 18px; height: 18px; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 0 0 1px #ddd; flex-shrink: 0; }

.fp-presets { display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 8px; align-items: center; }
.preset-label { font-size: 11px; color: #8892a6; flex-shrink: 0; }
.preset-chip {
  font-size: 11px; padding: 1px 8px; border-radius: 3px; cursor: pointer;
  background: #f5f6f8; color: #606266; border: 1px solid #e4e7ed; font-family: monospace;
  transition: all .12s; line-height: 22px;
}
.preset-chip:hover { border-color: #4e7cff; color: #4e7cff; background: #f0f4ff; }

.fp-range { display: flex; align-items: center; gap: 4px; margin-bottom: 8px; flex-wrap: wrap; }
.range-label { font-size: 12px; color: #8892a6; font-weight: 600; }
.range-sep { color: #c0c4cc; }
.range-val { font-size: 11px; color: #606266; font-family: monospace; }

.fp-canvas-wrap { position: relative; margin-bottom: 8px; }
.fp-canvas-wrap canvas {
  width: 100%; height: auto; display: block;
  border: 1px solid #e4e7ed; border-radius: 8px; background: #fff;
}
.fp-cursor {
  position: absolute; top: 8px; right: 12px;
  font-size: 11px; color: #4e7cff; font-family: monospace;
  background: rgba(255,255,255,.92); padding: 4px 10px; border-radius: 4px;
  border: 1px solid #e4e7ed; pointer-events: none; max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

.fp-list { display: flex; flex-direction: column; gap: 2px; margin-bottom: 8px; }
.fp-fn-row { display: flex; align-items: center; gap: 8px; padding: 3px 0; }
.fp-fn-dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
.fp-fn-expr { font-size: 13px; font-family: monospace; color: #303133; }
.fp-fn-expr.dim { color: #c0c4cc; }
.fp-fn-acts { margin-left: auto; display: flex; gap: 4px; }
.fp-fn-acts .del { color: #f56c6c; }

.fp-actions { display: flex; align-items: center; gap: 8px; }
.fp-hint { font-size: 11px; color: #c0c4cc; margin-left: auto; }
.fp-error { margin-top: 6px; font-size: 12px; color: #f56c6c; }
</style>
